import { ActivityEntityName } from '../shared/activity.entity.name';
import { EntityLink } from '../entity';
import { Bill, InvoiceProcessingType } from './invoicing';
import { InvoiceStatus } from './invoicing.enum';

/**
 * Create/update allowlist (production contract).
 * Number, status, version, integration, settlement, totals, audit, PO/AI provenance are server-owned.
 */
export const BILL_MUTABLE_FIELDS = [
  'contact',
  'billingCompany',
  'job',
  'workOrder',
  'invoiceDate',
  'dueDate',
  'customerReference',
  'notes',
  'terms',
  'taxInclusiveOption',
  'taxRate',
  'lines',
  'attachments',
  'theme',
  'pricebook',
  'timezone',
  'type',
  'resources',
  'links',
] as const;

export type BillMutableField = (typeof BILL_MUTABLE_FIELDS)[number];

/** Fields clients must never set via generic save/patch. */
export const BILL_PROTECTED_FIELDS = [
  'invoiceNumber',
  'status',
  'subStatus',
  'version',
  'integration',
  'integrationId',
  'subTotal',
  'tax',
  'total',
  'prepaymentSubTotal',
  'prepaymentTax',
  'prepaymentTotal',
  'amountPaid',
  'amountRefunded',
  'lastPaymentDate',
  'isReconcile',
  'outstandingAmount',
  'creditAmount',
  'roundingAmount',
  'payments',
  'hasPayments',
  'hasAllocation',
  'copiedFromSource',
  'source',
  'creatorContext',
  'generatedDate',
  'lastIssuedDate',
  'modifiedAfterSend',
  'pdf',
  'timesheetIds',
  'systemTags',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'active',
] as const;

export type BillProtectedField = (typeof BILL_PROTECTED_FIELDS)[number];

export type BillSubmitIntent = 'saveDraft' | 'approve' | 'approveAndSend';

export const BILL_SUBMIT_INTENT_KEY = 'submitIntent';

const BILL_VIRTUAL_BINDS = ['billSupplier', 'billSite', 'billContact', '$variables', BILL_SUBMIT_INTENT_KEY] as const;

const SERVER_OWNED_BILL_LINK_TYPES = new Set<string>([
  ActivityEntityName.PurchaseOrder,
  ActivityEntityName.Job,
  ActivityEntityName.WorkOrder,
  ActivityEntityName.Invoice,
]);

const IDENTITY_FIELDS = ['_id', 'type', 'contact', 'job', 'workOrder', 'links', 'timezone', 'billingCompany'] as const;

export function listForbiddenBillPatchFields(patch: Partial<Bill> | Record<string, unknown> | null | undefined): string[] {

  if (!patch) return [];
  return BILL_PROTECTED_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(patch, field));
}

export function assertBillPatchAllowlist(patch: Partial<Bill> | Record<string, unknown> | null | undefined): void {

  const forbidden = listForbiddenBillPatchFields(patch);
  if (forbidden.length > 0) {
    throw new Error(`Bill patch rejected protected fields: ${forbidden.join(', ')}`);
  }
}

/** Date-typed fields accepted from clients. Wire format is an ISO string (or Date/null). */
export const BILL_DATE_FIELDS = ['invoiceDate', 'dueDate'] as const;

/**
 * Coerce ISO strings/numbers to Dates and reject anything uncastable (e.g. a Date instance
 * degraded to `{}` by spread-cloning) so it fails loudly instead of corrupting the write.
 */
export function normalizeBillDates(patch: Partial<Bill> | null | undefined): Partial<Bill> {

  const result = { ...(patch ?? {}) } as Record<string, unknown>;
  for (const field of BILL_DATE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(result, field)) continue;
    const value = result[field];
    if (value === null || value === undefined) continue;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) throw new Error(`${field} is not a valid date`);
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) throw new Error(`${field} is not a valid date`);
      result[field] = parsed;
      continue;
    }
    throw new Error(`${field} is not a valid date value`);
  }
  return result as Partial<Bill>;
}

export function pickBillMutableFields(source: Partial<Bill> | Record<string, unknown> | null | undefined): Partial<Bill> {

  const result: Partial<Bill> = {};
  if (!source) return result;

  for (const field of BILL_MUTABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      (result as Record<string, unknown>)[field] = (source as Record<string, unknown>)[field];
    }
  }
  return normalizeBillDates(result);
}

export function stripBillVirtualBinds(model: Record<string, unknown>): Record<string, unknown> {

  const result = { ...model };
  for (const key of BILL_VIRTUAL_BINDS) delete result[key];
  return result;
}

/** Restore mounted identity when Surface bind projection omitted it. Explicit nulls stay null. */
export function mergeMountedBillIdentity(submitted: Record<string, unknown>, mounted: Record<string, unknown> | null | undefined): Record<string, unknown> {

  const source = mounted ?? {};
  const result = { ...submitted };
  for (const key of IDENTITY_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(submitted, key)) continue;
    if (source[key] != null && source[key] !== '') result[key] = source[key];
  }
  return result;
}

export function readBillSubmitIntent(model: Record<string, unknown> | null | undefined): BillSubmitIntent {

  const vars = model?.['$variables'] as Record<string, unknown> | undefined;
  const raw = vars?.[BILL_SUBMIT_INTENT_KEY] ?? vars?.['billSubmitIntent'] ?? model?.[BILL_SUBMIT_INTENT_KEY];
  if (raw === 'approveAndSend' || raw === InvoiceProcessingType.ApprovedAndSend) return 'approveAndSend';
  if (raw === 'approve' || raw === InvoiceProcessingType.Approved) return 'approve';
  return 'saveDraft';
}

export function toBillProcessingType(intent: BillSubmitIntent): InvoiceProcessingType {

  if (intent === 'approveAndSend') return InvoiceProcessingType.ApprovedAndSend;
  if (intent === 'approve') return InvoiceProcessingType.Approved;
  return InvoiceProcessingType.Draft;
}

/** Draft is fully editable; Approved is conditional (Manage Approved Bills — 10498). */
export function isBillEditable(status: InvoiceStatus | undefined | null): boolean {

  return status == null || status === InvoiceStatus.Draft;
}

export function assertBillEditable(status: InvoiceStatus | undefined | null): void {

  if (!isBillEditable(status)) {
    throw new Error(`Bill is not editable in status ${status}`);
  }
}

/** Draft disposal is Void, not Delete. */
export function canDeleteBill(_status?: InvoiceStatus | undefined | null): boolean {

  return false;
}

export function assertBillDeletable(_status?: InvoiceStatus | undefined | null): never {

  throw new Error('Bill draft disposal uses Void, not Delete');
}

/** PO / Job / Stage / Invoice lineage stays server-owned on generic save/patch. */
export function sanitizeBillClientLinks(links: EntityLink[] | undefined, existing?: EntityLink[]): EntityLink[] {

  const preserved = (existing ?? []).filter(isServerOwnedBillLink);
  const clientLinks = (links ?? []).filter(link => !isServerOwnedBillLink(link));
  return [...clientLinks, ...preserved];
}

export function isServerOwnedBillLink(link: EntityLink | null | undefined): boolean {

  return !!link?.type && SERVER_OWNED_BILL_LINK_TYPES.has(link.type);
}

export function assertBillWriteInvariants(bill: Partial<Bill> | null | undefined): void {

  if (!bill) return;
  const billDate = bill.invoiceDate ? new Date(bill.invoiceDate as Date) : null;
  const dueDate = bill.dueDate ? new Date(bill.dueDate as Date) : null;
  if (billDate && Number.isNaN(billDate.getTime())) throw new Error('invoiceDate is not a valid date');
  if (dueDate && Number.isNaN(dueDate.getTime())) throw new Error('dueDate is not a valid date');
  if (billDate && dueDate && dueDate.getTime() < billDate.getTime()) {
    throw new Error('Due date cannot precede bill date');
  }
}

const LINKED_LINE_LOCKED_FIELDS = ['name', 'quantity', 'price', 'itemLink', 'catalogueCodeId', 'uom', 'codeUoM'] as const;

export function sanitizeBillLinkedLines(next: Bill['lines'] | undefined, existing?: Bill['lines']): Bill['lines'] | undefined {

  if (!next) return next;
  const previous = new Map((existing ?? []).filter(line => line?._id).map(line => [line._id, line]));
  return next.map(line => {
    const prior = previous.get(line._id);
    if (!prior?.itemLink && !(prior as { copiedFromSource?: unknown } | undefined)) return line;
    if (!prior?.itemLink) return line;
    const locked = { ...line };
    for (const field of LINKED_LINE_LOCKED_FIELDS) {
      (locked as unknown as Record<string, unknown>)[field] = (prior as unknown as Record<string, unknown>)[field];
    }
    return locked;
  });
}
